import { useState, useEffect, useCallback } from 'react'
import { Shield, AlertTriangle, Flame, Activity, Globe, Zap, Clock } from 'lucide-react'
import type { MonitorStats } from '@/lib/api'

interface StatsBarProps {
  stats: MonitorStats | null
  loading?: boolean
  onFilterBySeverity?: (severity: string | null) => void
  activeSeverityFilter?: string | null
}

const CLOCKS: { label: string; tz: string }[] = [
  { label: 'UTC', tz: 'UTC' },
  { label: 'NYC', tz: 'America/New_York' },
  { label: 'LDN', tz: 'Europe/London' },
  { label: 'TYO', tz: 'Asia/Tokyo' },
  { label: 'MSK', tz: 'Europe/Moscow' },
]

function WorldClocks() {
  const [times, setTimes] = useState<string[]>([])

  useEffect(() => {
    const update = () => {
      setTimes(
        CLOCKS.map(({ tz }) =>
          new Date().toLocaleTimeString('en-US', {
            timeZone: tz,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        )
      )
    }
    update()
    const id = setInterval(update, 10_000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="flex items-center gap-4">
      {CLOCKS.map(({ label }, i) => (
        <div key={label} className="flex items-center gap-1.5 text-xs font-mono">
          <span className="text-gray-600">{label}</span>
          <span className="text-green-400/90 tabular-nums">{times[i] || '--:--'}</span>
        </div>
      ))}
    </div>
  )
}

export default function StatsBar({ stats, loading, onFilterBySeverity, activeSeverityFilter }: StatsBarProps) {
  if (loading || !stats) {
    return (
      <div className="flex items-center gap-6 px-4 py-2 bg-gray-950 border-b border-green-900/40">
        <div className="h-4 w-48 bg-gray-800/40 rounded animate-pulse" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-4 w-20 bg-gray-800/40 rounded animate-pulse" />
        ))}
      </div>
    )
  }

  const criticalCount = stats.events_by_severity?.critical || 0
  const highCount = stats.events_by_severity?.high || 0
  const mediumCount = stats.events_by_severity?.medium || 0
  const cyberCount = stats.events_by_domain?.cyber || 0
  const geoCount = stats.events_by_domain?.geopolitical || 0

  const handleSeverityClick = useCallback((sev: string) => {
    if (!onFilterBySeverity) return
    onFilterBySeverity(activeSeverityFilter === sev ? null : sev)
  }, [onFilterBySeverity, activeSeverityFilter])

  const iconSize = 'w-4 h-4'
  const badges = [
    { key: 'total', icon: <Globe className={iconSize} />, label: 'Total', value: stats.total_events, color: 'text-green-400', border: 'border-green-500/40', bg: 'bg-green-500/10' },
    { key: '24h', icon: <Activity className={iconSize} />, label: '24h', value: stats.events_last_24h, color: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/5' },
    { key: 'critical', icon: <AlertTriangle className={iconSize} />, label: 'Critical', value: criticalCount, color: 'text-red-400', border: 'border-red-500/40', bg: criticalCount > 0 ? 'bg-red-500/10' : 'bg-red-500/5', pulse: criticalCount > 0, severity: 'critical' },
    { key: 'high', icon: <Flame className={iconSize} />, label: 'High', value: highCount, color: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/5', severity: 'high' },
    { key: 'medium', icon: <Shield className={iconSize} />, label: 'Med', value: mediumCount, color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/5', severity: 'medium' },
    { key: 'cyber', icon: <Shield className={iconSize} />, label: 'Cyber', value: cyberCount, color: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/5' },
    { key: 'geo', icon: <Zap className={iconSize} />, label: 'Geo', value: geoCount, color: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/5' },
  ]

  return (
    <div className="flex items-center px-4 py-2.5 bg-gray-950 border-b border-green-900/40 gap-3 overflow-x-auto shadow-[0_0_20px_rgba(34,197,94,0.08)]">
      {/* Title */}
      <div className="flex-shrink-0 flex items-center gap-2.5 pr-4 border-r border-green-500/30 mr-1">
        <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse shadow-[0_0_10px_rgba(74,222,128,0.8)]" />
        <span className="text-sm font-bold text-green-400 uppercase tracking-[0.15em] whitespace-nowrap font-mono drop-shadow-[0_0_8px_rgba(74,222,128,0.4)]">
          SYCEK
        </span>
        <span className="text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap hidden xl:block">
          Geopolitical & Cyber Dashboard
        </span>
      </div>

      {/* Stat badges */}
      {badges.map((b) => {
        const isActive = b.severity && activeSeverityFilter === b.severity
        const clickable = !!b.severity
        return (
          <button
            key={b.key}
            onClick={() => b.severity && handleSeverityClick(b.severity)}
            disabled={!clickable}
            className={`flex items-center gap-2 px-2.5 py-1 rounded border text-xs whitespace-nowrap transition-all ${b.bg} ${b.border} ${
              isActive ? 'ring-1 ring-white/30 scale-105' : ''
            } ${clickable ? 'cursor-pointer hover:scale-105 hover:brightness-125' : 'cursor-default'}`}
          >
            <span className={b.color}>{b.icon}</span>
            <span className="text-gray-500 font-mono text-[11px]">{b.label}</span>
            <span className={`font-bold tabular-nums font-mono text-[11px] ${b.color} ${b.pulse ? 'animate-pulse' : ''}`}>
              {b.value.toLocaleString()}
            </span>
          </button>
        )
      })}

      {/* Credits */}
      <div className="flex-shrink-0 flex items-center gap-1.5 text-xs text-gray-500 pl-2 border-l border-green-900/40">
        <span>Made by</span>
        <a
          href="https://sycek.io"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-green-400 hover:text-green-300 underline decoration-green-500/60 underline-offset-1 drop-shadow-[0_0_6px_rgba(74,222,128,0.3)]"
        >
          Sycek.io
        </a>
        <span>&</span>
        <a
          href="https://falconfeeds.io"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-green-400/90 hover:text-green-300 underline decoration-green-500/50 underline-offset-1"
        >
          Falconfeeds.io
        </a>
      </div>

      {/* World clocks */}
      <div className="ml-auto flex-shrink-0 flex items-center gap-4 pl-4 border-l border-green-900/40">
        <Clock className="w-4 h-4 text-gray-600" />
        <WorldClocks />
        {stats.last_ingestion && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600 font-mono pl-2 border-l border-gray-800/50">
            <span className="w-2 h-2 rounded-full bg-emerald-500/60 shadow-[0_0_4px_rgba(16,185,129,0.4)]" />
            {new Date(stats.last_ingestion).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  )
}
